import React, { useState } from 'react';
import { authAPI } from '../api';
import type { User } from '../types';
import { Lock, Mail, AlertCircle } from 'lucide-react';
import { GovFlowLogo } from './GovFlowLogo';

interface LoginProps {
  onLoginSuccess: (token: string, user: User) => void;
}

export const Login: React.FC<LoginProps> = ({ onLoginSuccess }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const data = await authAPI.login(email, password);
      localStorage.setItem('gab_token', data.token);
      onLoginSuccess(data.token, data.user);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Erro ao realizar login.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center p-4 relative overflow-hidden">
      {/* Background Decorative Gradients */}
      <div className="absolute top-[-20%] left-[-10%] w-[600px] h-[600px] rounded-full bg-blue-900/20 blur-[120px] pointer-events-none"></div>
      <div className="absolute bottom-[-20%] right-[-10%] w-[600px] h-[600px] rounded-full bg-red-950/20 blur-[120px] pointer-events-none"></div>

      <div className="w-full max-w-md relative z-10">
        {/* Logo / Header */}
        <div className="text-center mb-8">
          {/* Anel de fundo com gradiente da marca */}
          <div className="inline-flex p-4 rounded-3xl mb-5 border border-[#1565C0]/20"
            style={{ background: 'linear-gradient(135deg, rgba(27,45,114,0.9) 0%, rgba(21,101,192,0.9) 60%, rgba(0,196,207,0.7) 100%)' }}
          >
            <GovFlowLogo variant="icon" dark size="md" />
          </div>
          <h1 className="text-4xl font-black tracking-tight font-sans mb-1">
            <span style={{ color: '#FFFFFF' }}>Gov</span><span style={{ color: '#60A5FA' }}>Flow</span>
          </h1>
          <p className="text-[10px] tracking-[0.25em] uppercase font-semibold text-slate-400 mb-2">
            Gestão Inteligente de Demandas Públicas
          </p>
        </div>

        {/* Card */}
        <div className="bg-slate-900/80 backdrop-blur-xl border border-slate-800/80 rounded-3xl p-8 shadow-2xl">
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-red-950/50 border border-red-800/50 rounded-xl p-3 flex items-start gap-2.5 text-red-300 text-sm">
                <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            <div>
              <label className="block text-slate-300 text-sm font-medium mb-2">E-mail Corporativo</label>
              <div className="relative">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500 w-5 h-5" />
                <input
                  type="email"
                  required
                  placeholder="exemplo@gabinete.com.br"
                  className="w-full bg-slate-950 border border-slate-800 rounded-xl py-3 pl-11 pr-4 text-white placeholder-slate-600 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all text-sm"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
            </div>

            <div>
              <label className="block text-slate-300 text-sm font-medium mb-2">Senha de Acesso</label>
              <div className="relative">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500 w-5 h-5" />
                <input
                  type="password"
                  required
                  placeholder="••••••••"
                  className="w-full bg-slate-950 border border-slate-800 rounded-xl py-3 pl-11 pr-4 text-white placeholder-slate-600 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all text-sm"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3.5 bg-blue-600 hover:bg-blue-500 active:bg-blue-700 disabled:bg-blue-800 text-white font-semibold rounded-xl transition-all shadow-lg shadow-blue-500/10 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50"
            >
              {loading ? 'Autenticando...' : 'Acessar Gabinete'}
            </button>
          </form>


        </div>

        <div className="text-center mt-6">
          <a
            href="/consulta-protocolo"
            className="text-sm text-slate-500 hover:text-slate-300 hover:underline transition-all"
          >
            Acessar Consulta Pública de Protocolo
          </a>
        </div>
      </div>
    </div>
  );
};
